import { useCallback, useEffect, useRef, useState } from 'react';
import { ShopApi } from '@/api/apis/ShopApi';

/**
 * Finns det något KÖPBART i stjärnbutiken just nu?
 *
 * Används för att villkora ingångarna till butiken i barnvyn. En butiksknapp som leder
 * till en tom hylla är ett löfte som inte infrias — barnet trycker, får ingenting, och
 * lär sig att knappen ljuger. Ingen vara alls = ingen ingång.
 *
 * KÖPBART = `stock === null` (obegränsat lager) ELLER `stock > 0`. En vara med
 * `stock === 0` finns kvar i butiken för föräldern men går inte att köpa, och räknas
 * därför inte som något att gå in för.
 *
 * LADDNINGSLÄGET är avsiktligt "false" tills svaret landat. Att visa knappen först och
 * ta bort den en halv sekund senare är värre än att den dyker upp: ett element som
 * försvinner under fingret bryter förutsägbarheten i en NPF-vy. Vi renderar alltså först
 * när vi VET att det finns något.
 *
 * `listShopItems` sväljer fel internt och returnerar undefined. Vi behåller då senast
 * kända värde istället för att gömma ingången: ett tillfälligt nätverksfel ska inte
 * plocka bort butiken för ett barn som redan ser den.
 *
 * TVÅ KAPPLÖPNINGSSKYDD (samma som usePendingPurchaseCount):
 * 1. Varje anrop får ett löpnummer. Landar ett långsamt tidigare svar efter ett snabbare
 *    senare ignoreras det — annars kan ingången slås tillbaka till ett äldre läge.
 * 2. Svar som kommer in efter unmount skrivs inte (ingen setState på avmonterad komponent).
 *
 * Läses om när fliken/fönstret blir synligt igen. Utan det står barnvyn kvar utan ingång
 * hur länge som helst efter att föräldern lagt in den första varan i handledarläget.
 *
 * `prices` är samma köpbara varor uttryckta som SORTERADE priser, och finns för att
 * tonåringens dagsida ska kunna visa hur långt det är kvar till nästa sak i butiken.
 * Den härleds ur exakt samma svar som `hasItems` — att fråga en gång till för den
 * uppgiften hade varit ett andra anrop för data vi redan har i handen.
 */
export const useShopHasItems = (): {
  hasItems: boolean;
  /** Priser (stjärnor) för allt köpbart, lägst först. Tom när butiken är tom. */
  prices: number[];
  reload: () => Promise<void>;
} => {
  const [hasItems, setHasItems] = useState<boolean>(false);
  const [prices, setPrices] = useState<number[]>([]);
  const latestRequest = useRef<number>(0);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    const result = await new ShopApi().listShopItems({});

    // Inte längre det senaste anropet, eller komponenten är borta → släng svaret.
    if (!mounted.current || latestRequest.current !== requestId) return;
    if (result !== undefined) {
      const purchasable = result.filter((item) => item.stock === null || item.stock > 0);
      setHasItems(purchasable.length > 0);
      setPrices(purchasable.map((item) => item.priceStars).sort((a, b) => a - b));
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [reload]);

  return { hasItems, prices, reload };
};
